import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  Popover,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  TextField,
  InputAdornment,
  Badge,
  alpha,
  useTheme,
} from "@mui/material";
import TagIcon from "@mui/icons-material/Tag";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getTeamSpaceLabel } from "./teamSpaceLabel";
import { getInitials } from "../../utils/utils";
import { openTeamPopupWindow } from "../../utils/openTeamPopupWindow";
import TeamPresenceAvatar from "../TeamPresenceAvatar";
import TeamPresenceRoster from "../TeamPresenceRoster";
import SidebarSection from "./SidebarSection";
import CreateChannelDialog from "../../pages/Team/components/CreateChannelDialog";
import StartConversationDialog from "../../pages/Team/components/StartConversationDialog";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { findUserDmSpace } from "../../pages/Team/utils/teamUiUtils";
import TeamSpaceListSubtitle from "../../pages/Team/components/TeamSpaceListSubtitle";
import { teamTasksPaths } from "../../utils/teamNavigation";
import { useActiveVideoRooms } from "../../hooks/useActiveVideoRooms";
import { ActiveVideoIndicator } from "../ActiveVideo/ActiveVideoIndicator";
import { isEntityDiscussionSpace } from "../../pages/Team/utils/teamMentions";
import { partitionTeamSidebarSpaces, sumSpaceUnreadCount } from "../../pages/Team/utils/teamSpaceGroups";

const COLLAPSE_STORAGE_KEY = "xproTeamFabPickerCollapsed";

function loadCollapsedState() {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (!raw) return { channels: false, requestChannels: false, dms: false };
    const parsed = JSON.parse(raw);
    return {
      channels: Boolean(parsed.channels),
      requestChannels: Boolean(parsed.requestChannels),
      dms: Boolean(parsed.dms),
    };
  } catch {
    return { channels: false, requestChannels: false, dms: false };
  }
}

const UnreadBadge = ({ count }) => {
  if (!count) return null;
  return (
    <Box
      sx={{
        minWidth: 18,
        height: 18,
        px: 0.5,
        borderRadius: 9,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        fontSize: "0.65rem",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {count > 9 ? "9+" : count}
    </Box>
  );
};

const TeamChatSpacePicker = ({
  anchorEl,
  open,
  onClose,
  spaces,
  onSelectSpace,
  onCreateChannel,
  onCreateDm,
  isCreating,
  canCreateChannel,
  currentUserId,
  onRefresh,
}) => {
  const { t } = useTranslation("team");
  const { t: tTasks } = useTranslation("teamTasks");
  const theme = useTheme();
  const navigate = useNavigate();
  const tasksPaths = useMemo(() => teamTasksPaths(), []);

  const [search, setSearch] = useState("");
  const [channelOpen, setChannelOpen] = useState(false);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [collapsed, setCollapsed] = useState(loadCollapsedState);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { byTeamSpaceId } = useActiveVideoRooms({ enabled: open });

  const toggleSection = useCallback((key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const unreadTotal = useMemo(
    () => (spaces || []).reduce((sum, s) => sum + (Number(s.unreadCount) || 0), 0),
    [spaces]
  );

  const matchesSearch = (space) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const label = getTeamSpaceLabel(space).toLowerCase();
    const preview = String(space.lastMessagePreview || "").toLowerCase();
    return label.includes(q) || preview.includes(q);
  };

  const { channels, requestChannels, dms } = useMemo(
    () => partitionTeamSidebarSpaces(spaces, matchesSearch),
    [spaces, search]
  );
  const channelsUnread = useMemo(() => sumSpaceUnreadCount(channels), [channels]);
  const requestChannelsUnread = useMemo(() => sumSpaceUnreadCount(requestChannels), [requestChannels]);
  const dmsUnread = useMemo(() => sumSpaceUnreadCount(dms), [dms]);

  const handleRefresh = useCallback(async () => {
    if (typeof onRefresh !== "function" || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch {
      /* keep current list; user can retry */
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, isRefreshing]);

  const handleStartConversation = async (payload) => {
    if (!payload || !onCreateDm) return;
    try {
      const res = await onCreateDm(payload);
      if (res?.space) {
        onSelectSpace(res.space);
      } else if (res?.id) {
        onSelectSpace({ id: res.id });
      }
      onClose();
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t("dmOpenError")));
      throw err;
    }
  };

  const handleOpenUserDm = useCallback(
    async (user) => {
      const uid = Number(user?.id);
      if (!Number.isFinite(uid) || uid <= 0) return;
      const existing = findUserDmSpace(spaces, uid);
      if (existing) {
        onSelectSpace(existing);
        onClose();
        return;
      }
      if (!onCreateDm) return;
      try {
        const res = await onCreateDm({ userId: uid });
        if (res?.space) {
          onSelectSpace(res.space);
        } else if (res?.id) {
          onSelectSpace({ id: res.id });
        }
        onClose();
      } catch (err) {
        setErrorMsg(getApiErrorMessage(err, t("dmOpenError")));
        throw err;
      }
    },
    [spaces, onSelectSpace, onClose, onCreateDm, t]
  );

  const handleCreateChannel = async (payload) => {
    if (!onCreateChannel) return;
    try {
      const res = await onCreateChannel(payload);
      if (res?.space) {
        onSelectSpace(res.space);
      } else if (res?.id) {
        onSelectSpace({ id: res.id });
      }
      onClose();
      return res;
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t("channelCreateError")));
      throw err;
    }
  };

  const renderChannelItem = (space) => {
    const label = getTeamSpaceLabel(space);
    const unread = Number(space.unreadCount) || 0;
    const showHash = !String(label).startsWith("#");

    return (
      <ListItemButton
        key={space.id}
        onClick={() => onSelectSpace(space)}
        sx={{
          mx: 1,
          mb: 0.25,
          borderRadius: 1.5,
          py: 0.85,
          color: "text.primary",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%", minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: "text.secondary",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            {isEntityDiscussionSpace(space) ? (
              <AssignmentOutlinedIcon sx={{ fontSize: 16 }} />
            ) : space.isPrivate ? (
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <TagIcon sx={{ fontSize: 16 }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.primary" fontWeight={unread > 0 ? 700 : 500} noWrap sx={{ flex: 1 }}>
                {showHash ? `#${label}` : label}
              </Typography>
              <ActiveVideoIndicator room={byTeamSpaceId.get(Number(space.id))} />
              <UnreadBadge count={unread} />
            </Box>
            <TeamSpaceListSubtitle
              spaceId={space.id}
              lastMessagePreview={space.lastMessagePreview}
            />
          </Box>
        </Box>
      </ListItemButton>
    );
  };

  const renderDmAvatar = (space, label) => {
    if (space.dmPeerType === "agent") {
      return (
        <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.secondary.main, 0.15), color: "secondary.main" }}>
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
        </Avatar>
      );
    }
    if (space.dmPeerType === "chatbot") {
      return (
        <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.info.main, 0.15), color: "info.main" }}>
          <SupportAgentOutlinedIcon sx={{ fontSize: 16 }} />
        </Avatar>
      );
    }
    if (space.dmPeerUserId) {
      return (
        <TeamPresenceAvatar
          userId={space.dmPeerUserId}
          src={space.dmPeerAvatarUrl}
          name={label}
          size={28}
          dotSize={8}
          sx={{
            "& .MuiAvatar-root": {
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: "primary.main",
            },
          }}
        />
      );
    }
    return (
      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: alpha(theme.palette.primary.main, 0.15), color: "primary.main" }}>
        {getInitials(label)}
      </Avatar>
    );
  };

  const renderDmItem = (space) => {
    const label = getTeamSpaceLabel(space);
    const unread = Number(space.unreadCount) || 0;

    return (
      <ListItemButton
        key={space.id}
        onClick={() => onSelectSpace(space)}
        sx={{
          mx: 1,
          mb: 0.25,
          borderRadius: 1.5,
          py: 0.85,
          color: "text.primary",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%", minWidth: 0 }}>
          {renderDmAvatar(space, label)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.primary" fontWeight={unread > 0 ? 700 : 500} noWrap sx={{ flex: 1 }}>
                {label}
              </Typography>
              <ActiveVideoIndicator room={byTeamSpaceId.get(Number(space.id))} />
              <UnreadBadge count={unread} />
            </Box>
            <TeamSpaceListSubtitle
              spaceId={space.id}
              lastMessagePreview={space.lastMessagePreview}
            />
          </Box>
        </Box>
      </ListItemButton>
    );
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              maxWidth: "calc(100vw - 24px)",
              maxHeight: "min(520px, calc(100vh - 100px))",
              display: "flex",
              flexDirection: "column",
              borderRadius: 2.5,
              overflow: "hidden",
              boxShadow: theme.shadows[12],
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 1.75,
            pb: 1.25,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.25 }}>
            <Badge
              color="error"
              badgeContent={unreadTotal > 0 ? (unreadTotal > 9 ? "9+" : unreadTotal) : 0}
              invisible={unreadTotal <= 0}
              overlap="circular"
            >
              <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
                <ForumOutlinedIcon sx={{ fontSize: 22 }} />
              </Avatar>
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
                {t("floatingPickerTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("sidebarSubtitle")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mt: -0.25 }}>
              {typeof onRefresh === "function" ? (
                <Tooltip title={t("refreshSpaces")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      aria-label={t("refreshSpaces")}
                      sx={{ color: "text.secondary" }}
                    >
                      <RefreshIcon
                        fontSize="small"
                        sx={
                          isRefreshing
                            ? {
                                animation: "teamSpacesRefreshSpin 0.8s linear infinite",
                                "@keyframes teamSpacesRefreshSpin": {
                                  to: { transform: "rotate(360deg)" },
                                },
                              }
                            : undefined
                        }
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <Tooltip title={tTasks("inboxTitleMine")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate(tasksPaths.inbox);
                  }}
                  aria-label={tTasks("inboxTitleMine")}
                  sx={{ color: "text.secondary" }}
                >
                  <AssignmentOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={tTasks("boardListTitle")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate(tasksPaths.list);
                  }}
                  aria-label={tTasks("boardListTitle")}
                  sx={{ color: "text.secondary" }}
                >
                  <ViewKanbanOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("openFullPage")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    onClose();
                    openTeamPopupWindow();
                  }}
                  aria-label={t("openFullPage")}
                  sx={{ color: "text.secondary" }}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TextField
            size="small"
            fullWidth
            placeholder={t("searchSpaces")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
          />

          {errorMsg ? (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {errorMsg}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 0.5 }}
          className="sidebar-scroll"
        >
          <SidebarSection
            title={t("directMessages")}
            count={dms.length}
            unreadCount={dmsUnread}
            collapsed={collapsed.dms}
            onToggleCollapse={() => toggleSection("dms")}
            showAdd={Boolean(onCreateDm)}
            onAdd={() => setConversationOpen(true)}
          >
            <List dense disablePadding>
              {dms.length ? (
                dms.map(renderDmItem)
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  {search ? t("noSearchResults") : t("noDms")}
                </Typography>
              )}
            </List>
          </SidebarSection>

          <SidebarSection
            title={t("channels")}
            count={channels.length}
            unreadCount={channelsUnread}
            collapsed={collapsed.channels}
            onToggleCollapse={() => toggleSection("channels")}
            showAdd={canCreateChannel && Boolean(onCreateChannel)}
            addLabel={t("newChannel")}
            onAdd={() => setChannelOpen(true)}
          >
            <List dense disablePadding>
              {channels.length ? (
                channels.map(renderChannelItem)
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  {search ? t("noSearchResults") : t("noChannels")}
                </Typography>
              )}
            </List>
          </SidebarSection>

          <SidebarSection
            title={t("requestChannels")}
            count={requestChannels.length}
            unreadCount={requestChannelsUnread}
            collapsed={collapsed.requestChannels}
            onToggleCollapse={() => toggleSection("requestChannels")}
          >
            <List dense disablePadding>
              {requestChannels.length ? (
                requestChannels.map(renderChannelItem)
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  {search ? t("noSearchResults") : t("noRequestChannels")}
                </Typography>
              )}
            </List>
          </SidebarSection>

          <TeamPresenceRoster
            compact
            inheritScroll
            currentUserId={currentUserId}
            onSelectUser={handleOpenUserDm}
          />
        </Box>
      </Popover>

      <CreateChannelDialog
        open={channelOpen}
        onClose={() => setChannelOpen(false)}
        onCreate={handleCreateChannel}
        isCreating={isCreating}
        onError={(err) => setErrorMsg(getApiErrorMessage(err, t("channelCreateError")))}
      />

      <StartConversationDialog
        open={conversationOpen}
        onClose={() => setConversationOpen(false)}
        onSelect={handleStartConversation}
        isBusy={isCreating}
      />
    </>
  );
};

export default TeamChatSpacePicker;
